import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import type { CheckTextDTO, MessageDTO, TranscribeResponseDTO } from '@tc/contracts';
import { READING_LOCALES } from '@tc/domain';
import { api, ApiError, apiUpload } from '../api.ts';
import { messageAction, send, sendTyping, useStore } from '../store.ts';
import { stopSpeaking } from '../tts.ts';
import { ACCEPT_ATTR, acceptable, fmtBytes, MAX_FILES, MAX_UPLOAD_BYTES, prepare, upload, type PendingFile } from '../attachments.ts';
import { fmtDuration, MAX_RECORDING_MS, Recorder, recordingSupported, toWav16k, type Recording } from '../voice.ts';
import { languageName, useT, useUiLocale } from '../i18n.ts';
import { displayName, readableBody } from './common.tsx';
import { EmojiPicker } from './EmojiPicker.tsx';

interface Props {
  conversationId: string;
  replyTo: MessageDTO | null;
  /** Part of replyTo being quoted (in the language it was shown). */
  quote?: { text: string; locale: string } | null;
  editing: MessageDTO | null;
  /** Text to put in the composer (e.g. a suggested clarification question); nonce re-triggers. */
  prefill?: { text: string; nonce: number } | null;
  /** Walkie-talkie: what you say is sent at once (no review step). */
  talkMode?: boolean;
  onDone: () => void;
}

const draftKey = (id: string) => `tc.draft.${id}`;

export function Composer({ conversationId, replyTo, quote = null, editing, prefill, talkMode = false, onDone }: Props) {
  const t = useT();
  const ui = useUiLocale();
  const enterToSend = useStore((s) => s.me?.enterToSend ?? true);
  const [text, setText] = useState('');
  const [sourceLocale, setSourceLocale] = useState<string>('');
  const [picker, setPicker] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  // ---- @mentions (groups): typing "@" opens a member picker; on send, names still in the text are mentioned.
  const myId = useStore((s) => s.me?.id);
  const conv = useStore((s) => s.conversations[conversationId]);
  const mentionable = conv?.type === 'group' ? conv.members.filter((m) => m.id !== myId && !m.disabled) : [];
  const [mention, setMention] = useState<{ start: number; end: number; q: string; sel: number } | null>(null);
  const mentionMatches = mention
    ? mentionable
        .filter((m) => {
          const q = mention.q.toLowerCase();
          return !q || m.displayName.toLowerCase().split(/\s+/).some((w) => w.startsWith(q)) || m.displayName.toLowerCase().startsWith(q);
        })
        .slice(0, 6)
    : [];
  const mentionsIn = (body: string) => mentionable.filter((m) => body.includes(`@${m.displayName}`)).map((m) => m.id);
  const findMention = (v: string, caret: number) => {
    if (!mentionable.length) return setMention(null);
    const before = v.slice(0, caret);
    const mm = /(^|\s)@([^\s@]{0,30})$/.exec(before);
    setMention(mm ? { start: caret - mm[2]!.length - 1, end: caret, q: mm[2]!, sel: 0 } : null);
  };
  const pickMention = (name: string) => {
    if (!mention) return;
    const insert = `@${name} `;
    const next = text.slice(0, mention.start) + insert + text.slice(mention.end);
    const pos = mention.start + insert.length;
    setMention(null);
    update(next);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(pos, pos);
    });
  };
  // ---- voice: click the mic, speak, click again → the text appears here to check before sending.
  const speech = useStore((s) => s.speech);
  const readingLocale = useStore((s) => s.me?.readingLocale);
  const micAvailable = speech !== 'disabled' && recordingSupported();
  const recorder = useRef<Recorder | null>(null);
  const [voice, setVoice] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [recording, setRecording] = useState<Recording | null>(null);
  const [attachAudio, setAttachAudio] = useState(false);
  const [sending, setSending] = useState(false);
  // ---- spelling check before sending
  const textCheck = useStore((s) => s.me?.textCheck ?? 'ask');
  const provider = useStore((s) => s.provider);
  const [fix, setFix] = useState<Pick<CheckTextDTO, 'corrected' | 'changes'> | null>(null);
  const [autoNote, setAutoNote] = useState<string | null>(null);
  // ---- attachments: screenshots (paste), drag & drop, file/photo picker.
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const addFiles = async (list: FileList | File[]) => {
    setFileError(null);
    const arr = [...list];
    const ok = arr.filter(acceptable);
    if (ok.length < arr.length) setFileError(t('fileTypeNotAllowed'));
    const room = MAX_FILES - files.length;
    if (ok.length > room) setFileError(t('tooManyFiles', { n: MAX_FILES }));
    const prepared = await Promise.all(ok.slice(0, Math.max(0, room)).map(prepare));
    const big = prepared.filter((p) => p.file.size > MAX_UPLOAD_BYTES);
    if (big.length) setFileError(t('fileTooLarge', { size: fmtBytes(MAX_UPLOAD_BYTES) }));
    setFiles((cur) => [...cur, ...prepared.filter((p) => p.file.size <= MAX_UPLOAD_BYTES)]);
    ref.current?.focus();
  };
  const removeFile = (key: string) =>
    setFiles((cur) => {
      const f = cur.find((x) => x.key === key);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return cur.filter((x) => x.key !== key);
    });
  // Drops anywhere on the open chat arrive here (Thread dispatches 'tc:attach').
  useEffect(() => {
    const on = (e: Event) => void addFiles((e as CustomEvent<File[]>).detail);
    window.addEventListener('tc:attach', on);
    return () => window.removeEventListener('tc:attach', on);
  });
  useEffect(() => {
    setFiles([]);
    setFileError(null);
  }, [conversationId]);

  // stopRecording can run from the auto-stop timer (a stale closure): read the latest text via a ref.
  const textRef = useRef(text);
  textRef.current = text;
  /** The text in the box came (at least partly) from the microphone. */
  const spokenRef = useRef(false);

  useEffect(() => {
    if (voice !== 'recording') return;
    const iv = setInterval(() => {
      setElapsed(recorder.current?.elapsedMs() ?? 0);
      setLevel(recorder.current?.level() ?? 0);
    }, 120);
    return () => clearInterval(iv);
  }, [voice]);

  // A new conversation starts without a pending recording; leaving cancels an ongoing one.
  useEffect(() => {
    setRecording(null);
    setAttachAudio(false);
    setVoiceError(null);
    return () => recorder.current?.cancel();
  }, [conversationId]);

  const talkRef = useRef(talkMode);
  talkRef.current = talkMode;

  // Hold-to-talk: press and hold the mic, release to send it for transcription, slide up/left to cancel.
  // A quick tap keeps the click-to-start / click-to-stop behaviour.
  const hold = useRef<{ t: number; x: number; y: number; released: boolean; cancel: boolean } | null>(null);
  const [holding, setHolding] = useState<'off' | 'on' | 'cancel'>('off');
  const onMicDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 || voice !== 'idle') return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const h0 = { t: Date.now(), x: e.clientX, y: e.clientY, released: false, cancel: false };
    hold.current = h0;
    // Still pressed after 450 ms: it is a hold — show "release to send".
    setTimeout(() => {
      if (hold.current === h0 && !h0.released) setHolding(h0.cancel ? 'cancel' : 'on');
    }, 460);
    void startRecording().then(() => {
      const h = hold.current;
      // Released (after holding) before the microphone was ready.
      if (h?.released) {
        hold.current = null;
        if (h.cancel) cancelRecording();
        else void stopRecording();
      }
    });
  };
  const onMicMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const h = hold.current;
    if (!h || h.released) return;
    if (Date.now() - h.t > 300) setHolding(h.cancel ? 'cancel' : 'on');
    const away = h.y - e.clientY > 70 || h.x - e.clientX > 90;
    if (away !== h.cancel) {
      h.cancel = away;
      setHolding(away ? 'cancel' : 'on');
    }
  };
  const onMicUp = () => {
    const h = hold.current;
    if (!h) return;
    h.released = true;
    setHolding('off');
    const held = Date.now() - h.t >= 450;
    if (!held) {
      hold.current = null; // a tap: recording continues until the stop button is clicked
      return;
    }
    if (!recorder.current) return; // still starting: handled when the microphone is ready
    hold.current = null;
    if (h.cancel) cancelRecording();
    else void stopRecording();
  };

  const startRecording = async () => {
    setVoiceError(null);
    stopSpeaking(); // don't record the other person's message being read aloud
    const r = new Recorder();
    try {
      await r.start(() => void stopRecording());
    } catch (err) {
      const name = (err as DOMException)?.name;
      setVoiceError(name === 'NotAllowedError' || name === 'SecurityError' ? t('micDenied') : name === 'NotFoundError' ? t('micMissing') : t('micFailed'));
      return;
    }
    recorder.current = r;
    setElapsed(0);
    setVoice('recording');
  };

  const stopRecording = async () => {
    const r = recorder.current;
    if (!r) return;
    recorder.current = null;
    setVoice('transcribing');
    const rec = await r.stop();
    if (!rec || rec.durationMs < 400) {
      setVoice('idle');
      return;
    }
    try {
      const wav = await toWav16k(rec.blob);
      const res = await apiUpload<TranscribeResponseDTO>(`/api/transcribe${readingLocale ? `?lang=${encodeURIComponent(readingLocale)}` : ''}`, wav, 'audio/wav');
      const said = res.text.trim();
      if (said && talkRef.current) {
        // Talk mode: send what was said straight away (the text stays visible in the chat).
        send(conversationId, said, replyTo?.id ?? null, sourceLocale || null, null, quote, true);
        onDone();
      } else if (said) {
        const el = ref.current;
        const cur = textRef.current;
        const start = el?.selectionStart ?? cur.length;
        const end = el?.selectionEnd ?? cur.length;
        const before = cur.slice(0, start);
        const sep = before && !/\s$/.test(before) ? ' ' : '';
        const next = before + sep + said + cur.slice(end);
        spokenRef.current = true;
        update(next);
        requestAnimationFrame(() => {
          el?.focus();
          const pos = (before + sep + said).length;
          el?.setSelectionRange(pos, pos);
        });
      } else {
        setVoiceError(t('speechEmpty'));
      }
      if (!talkRef.current) setRecording(rec);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setVoiceError(code === 'transcription_budget_exceeded' ? t('speechBudget') : t('speechFailed'));
    } finally {
      setVoice('idle');
    }
  };

  const cancelRecording = () => {
    recorder.current?.cancel();
    recorder.current = null;
    setVoice('idle');
  };

  // Per-conversation drafts survive reloads (device-local convenience only).
  useEffect(() => {
    if (editing) return;
    try {
      setText(localStorage.getItem(draftKey(conversationId)) ?? '');
    } catch {
      setText('');
    }
  }, [conversationId, editing]);

  useEffect(() => {
    if (editing) {
      setText(editing.body ?? '');
      setSourceLocale(editing.sourceLocaleDetected ? '' : editing.sourceLocale);
    }
    if (editing || replyTo) ref.current?.focus();
  }, [editing, replyTo]);

  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text);
    requestAnimationFrame(() => {
      const el = ref.current;
      el?.focus();
      el?.setSelectionRange(prefill.text.length, prefill.text.length);
    });
  }, [prefill]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [text]);

  const update = (v: string) => {
    setText(v);
    setFix(null);
    if (!v) spokenRef.current = false;
    else if (!editing) sendTyping(conversationId);
    if (!editing) {
      try {
        if (v) localStorage.setItem(draftKey(conversationId), v);
        else localStorage.removeItem(draftKey(conversationId));
      } catch {
        /* ignore */
      }
    }
  };

  const submit = async (opts: { skipCheck?: boolean; body?: string } = {}) => {
    let body = opts.body ?? text;
    if ((!body.trim() && !files.length) || sending || voice !== 'idle') return;
    setFix(null);
    // Pre-send spelling check: only interrupts when something clearly looks wrong.
    if (!opts.skipCheck && !editing && textCheck !== 'off' && provider !== 'disabled' && body.trim()) {
      setSending(true);
      const r = await api<CheckTextDTO>('POST', '/api/check', { text: body, sourceLocale: sourceLocale || null }, { timeoutMs: 6000 }).catch(() => null);
      setSending(false);
      if (r?.suspicious) {
        if (textCheck === 'auto') {
          body = r.corrected;
          setAutoNote(r.changes.map((c) => `${c.from} → ${c.to}`).join(', '));
          setTimeout(() => setAutoNote(null), 6000);
        } else {
          setFix({ corrected: r.corrected, changes: r.changes });
          return;
        }
      }
    }
    if (files.length && !editing) {
      // Upload each file, then send one message per file; the text goes with the last one as its caption.
      setSending(true);
      setFileError(null);
      const done: string[] = [];
      try {
        for (const [i, f] of files.entries()) {
          const mediaId = await upload(conversationId, f);
          const last = i === files.length - 1;
          send(conversationId, last ? body : '', i === 0 ? (replyTo?.id ?? null) : null, sourceLocale || null, mediaId, i === 0 ? quote : null, false, last ? mentionsIn(body) : []);
          done.push(f.key);
        }
      } catch {
        setFileError(t('uploadFailed'));
        setFiles((cur) => cur.filter((x) => !done.includes(x.key)));
        setSending(false);
        return;
      }
      files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      setFiles([]);
      setSending(false);
      update('');
      setText('');
      onDone();
      ref.current?.focus();
      return;
    }
    if (!body.trim()) return;
    if (editing) {
      try {
        await messageAction('PATCH', `/api/messages/${editing.id}`, { body, sourceLocale: sourceLocale || null, ...(mentionable.length ? { mentions: mentionsIn(body) } : {}) });
      } catch {
        return;
      }
    } else {
      let mediaId: string | null = null;
      if (recording && attachAudio) {
        // The recording must be uploaded first (needs a connection); the text itself can queue offline.
        setSending(true);
        try {
          const qs = `?durationMs=${Math.round(recording.durationMs)}`;
          mediaId = (await apiUpload<{ id: string }>(`/api/conversations/${conversationId}/audio${qs}`, recording.blob, recording.mime)).id;
        } catch {
          setVoiceError(t('audioUploadFailed'));
          setSending(false);
          return;
        }
        setSending(false);
      }
      send(conversationId, body, replyTo?.id ?? null, sourceLocale || null, mediaId, quote, spokenRef.current, mentionsIn(body));
      spokenRef.current = false;
      setRecording(null);
      setAttachAudio(false);
      setVoiceError(null);
    }
    update('');
    setText('');
    onDone();
    ref.current?.focus();
  };

  // Phone camera straight away (the "+" button opens the photo library and files instead).
  const takePhoto = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.setAttribute('capture', 'environment');
    input.onchange = () => {
      if (input.files) void addFiles(input.files);
    };
    input.click();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && mentionMatches.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const n = mentionMatches.length;
        setMention({ ...mention, sel: (mention.sel + (e.key === 'ArrowDown' ? 1 : n - 1)) % n });
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.nativeEvent.isComposing) {
        e.preventDefault();
        pickMention(mentionMatches[Math.min(mention.sel, mentionMatches.length - 1)]!.displayName);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    if (e.key === 'Escape' && (editing || replyTo)) {
      e.preventDefault();
      setText(editing ? '' : text);
      onDone();
      return;
    }
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return; // IME (pinyin) composition must not send
    // New line: Shift+Enter everywhere, and Option+Enter (Mac) / Alt+Enter (PC) like in many chat apps.
    // Browsers do not reliably insert a line break for Alt/Option+Enter, so insert it here.
    if (e.altKey) {
      e.preventDefault();
      const el = e.currentTarget;
      const start = el.selectionStart ?? text.length;
      const end = el.selectionEnd ?? text.length;
      // Insert in the field itself so the caret moves at once (a deferred caret move raced fast typing).
      el.setRangeText('\n', start, end, 'end');
      update(el.value);
      return;
    }
    const wantsSend = enterToSend ? !e.shiftKey : e.ctrlKey || e.metaKey;
    if (wantsSend) {
      e.preventDefault();
      void submit();
    }
  };

  const insertEmoji = (emoji: string) => {
    const el = ref.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    update(next);
    setPicker(false);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  return (
    <div className="composer">
      {(replyTo || editing) && (
        <div className="composer-context">
          <span>
            {editing ? t('editing') : t('replyingTo', { name: displayName(replyTo!.senderId, ui) })}
            {replyTo && (quote ? <span className="snippet quote"> ❝{quote.text}❞</span> : <span className="snippet"> {readableBody(replyTo)}</span>)}
          </span>
          <button
            className="icon-btn"
            onClick={() => {
              if (editing) setText('');
              onDone();
            }}
            aria-label={t('cancel')}
          >
            ✕
          </button>
        </div>
      )}
      {files.length > 0 && (
        <ul className="pending-files" aria-label={t('attachments')}>
          {files.map((f) => (
            <li key={f.key}>
              {f.preview ? <img src={f.preview} alt="" /> : <span className="file-icon" aria-hidden="true">PDF</span>}
              <span className="pf-name" title={f.name}>{f.name}</span>
              <span className="pf-size">{fmtBytes(f.file.size)}</span>
              <button type="button" className="icon-btn" onClick={() => removeFile(f.key)} aria-label={t('remove')}>✕</button>
            </li>
          ))}
          {sending && <li className="pf-status" role="status">{t('uploading')}</li>}
        </ul>
      )}
      {fileError && <div className="voice-bar error" role="alert">{fileError}</div>}
      {mention && mentionMatches.length > 0 && (
        <ul className="mention-picker" role="listbox" aria-label={t('mentionSomeone')}>
          {mentionMatches.map((m, i) => (
            <li key={m.id} role="option" aria-selected={i === mention.sel}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickMention(m.displayName)}>
                <strong>{m.displayName}</strong>
                {m.organizationName && <span className="muted small"> · {m.organizationName}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className={`composer-row ${text.trim() || files.length || editing ? 'has-content' : ''}`}>
        <div className="emoji-anchor">
          <button type="button" className="icon-btn" data-popup-toggle onClick={() => setPicker((v) => !v)} aria-label="Emoji" aria-expanded={picker}>☺︎</button>
          {picker && <EmojiPicker onPick={insertEmoji} onClose={() => setPicker(false)} />}
        </div>
        {!editing && (
          <>
            <button type="button" className="icon-btn attach" onClick={() => fileInput.current?.click()} aria-label={t('attach')} title={t('attach')}>
              <ClipIcon />
              <PlusIcon />
            </button>
            <input
              ref={fileInput}
              type="file"
              hidden
              multiple
              accept={ACCEPT_ATTR}
              onChange={(e) => {
                if (e.target.files) void addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </>
        )}
        <textarea
          ref={ref}
          rows={1}
          value={text}
          maxLength={8000}
          placeholder={t('typeMessage')}
          aria-label={t('typeMessage')}
          onChange={(e) => {
            update(e.target.value);
            findMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => setTimeout(() => setMention(null), 150)}
          onPaste={(e) => {
            const pasted = [...e.clipboardData.files].filter(acceptable);
            if (pasted.length && !editing) {
              e.preventDefault();
              void addFiles(pasted);
            }
          }}
        />
        {!editing && (
          <>
            <button type="button" className="icon-btn camera touch-only" onClick={takePhoto} aria-label={t('takePhoto')} title={t('takePhoto')}>
              <CameraIcon />
            </button>
          </>
        )}
        {micAvailable && !editing && (
          voice === 'recording' ? (
            <button
              type="button"
              className={`icon-btn mic on ${holding === 'cancel' ? 'cancel' : ''}`}
              onClick={() => !hold.current && void stopRecording()}
              onPointerMove={onMicMove}
              onPointerUp={onMicUp}
              onPointerCancel={onMicUp}
              aria-label={t('micStop')}
              title={t('micStop')}
            >
              <StopIcon />
            </button>
          ) : (
            <button
              type="button"
              className="icon-btn mic"
              onPointerDown={onMicDown}
              onPointerMove={onMicMove}
              onPointerUp={onMicUp}
              onPointerCancel={onMicUp}
              onContextMenu={(e) => e.preventDefault()}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), void startRecording())}
              disabled={voice === 'transcribing'}
              aria-label={t('micStart')}
              title={t('micHint')}
            >
              <MicIcon />
            </button>
          )
        )}
        <button className="btn primary send" onClick={() => void submit()} disabled={(!text.trim() && !files.length) || sending || voice !== 'idle'} aria-label={editing ? t('save') : t('send')}>
          <span className="send-label">{editing ? t('save') : t('send')}</span>
          <SendIcon editing={!!editing} />
        </button>
      </div>
      {fix && (
        <div className="fix-bar" role="alert">
          <span className="muted">{t('didYouMean')}</span>{' '}
          <span className="fix-text">{highlightChanges(fix.corrected, fix.changes)}</span>
          <div className="row wrap">
            <button type="button" className="btn small primary" onClick={() => void submit({ skipCheck: true, body: fix.corrected })}>{t('sendCorrected')}</button>
            <button type="button" className="btn small" onClick={() => void submit({ skipCheck: true })}>{t('sendAsIs')}</button>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                update(fix.corrected);
                ref.current?.focus();
              }}
            >
              {t('editText')}
            </button>
          </div>
        </div>
      )}
      {autoNote && <div className="voice-bar muted" role="status">{t('autoCorrected', { list: autoNote })}</div>}
      {voice === 'recording' && (
        <div className="voice-bar recording" role="status">
          <span className="rec-dot" aria-hidden="true" />
          <span>{holding === 'cancel' ? t('releaseToCancel') : holding === 'on' ? t('releaseToSend') : t('listening')}</span>
          <span className="rec-level" aria-hidden="true"><span style={{ transform: `scaleX(${0.08 + level * 0.92})` }} /></span>
          <span className="rec-time">{fmtDuration(elapsed)} / {fmtDuration(MAX_RECORDING_MS)}</span>
          <button type="button" className="btn small primary" onClick={() => void stopRecording()}>{t('micStop')}</button>
          <button type="button" className="link-btn" onClick={cancelRecording}>{t('cancel')}</button>
        </div>
      )}
      {voice === 'transcribing' && <div className="voice-bar" role="status"><span className="spinner" aria-hidden="true" /> {t('transcribing')}</div>}
      {voice === 'idle' && recording && !editing && (
        <div className="voice-bar">
          <label className="check">
            <input type="checkbox" checked={attachAudio} onChange={(e) => setAttachAudio(e.target.checked)} />
            <span>{t('sendWithAudio', { d: fmtDuration(recording.durationMs) })}</span>
          </label>
          <button type="button" className="link-btn" onClick={() => { setRecording(null); setAttachAudio(false); }}>{t('discardRecording')}</button>
        </div>
      )}
      {voiceError && <div className="voice-bar error" role="alert">{voiceError}</div>}
      <div className="composer-foot">
        <label>
          <span className="muted">{t('writingIn')}</span>{' '}
          <select value={sourceLocale} onChange={(e) => setSourceLocale(e.target.value)}>
            <option value="">{t('autoDetect')}</option>
            {READING_LOCALES.map((l) => (
              <option key={l} value={l}>{languageName(ui, l)}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

/** Phone layout: a round arrow (or a tick while editing) instead of the word. */
function SendIcon({ editing }: { editing: boolean }) {
  return (
    <svg className="send-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {editing ? <path d="M5 12.5l4.5 4.5L19 7.5" /> : <path d="M12 19V5M5.5 11.5L12 5l6.5 6.5" />}
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="plus-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 4.5v15M4.5 12h15" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 8.5a2 2 0 0 1 2-2h2.2l1.5-2h5.6l1.5 2h2.2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />
      <circle cx="12" cy="13" r="3.6" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3" y="3" width="10" height="10" rx="2" fill="currentColor" />
    </svg>
  );
}

function ClipIcon() {
  return (
    <svg className="clip-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.5 11.5l-8.3 8.3a5 5 0 0 1-7.1-7.1l8.6-8.6a3.4 3.4 0 0 1 4.8 4.8l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9" />
    </svg>
  );
}

/** Show the corrected text with the fixed words in bold. */
function highlightChanges(text: string, changes: Array<{ from: string; to: string }>) {
  const words = changes.map((c) => c.to).filter(Boolean);
  if (!words.length) return text;
  const re = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'giu');
  return text.split(re).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}
